import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useVertical } from '../context/VerticalContext';
import type { ProfitAndLossData } from '../types/finance';
import { ProfitAndLossCard } from '../components/dashboard/ProfitAndLossCard';
import { PartsVsLaborChart } from '../components/audit/PartsVsLaborChart';
import { Loader2, LayoutDashboard, PieChart, Download, Clock, TrendingUp } from 'lucide-react';
import { errorMessage, reportError } from '../lib/errors';

const TABS = [
    { id: 'pnl', label: 'Fiscal Dashboard', icon: LayoutDashboard },
    { id: 'charts', label: 'Parts vs. Labour', icon: PieChart },
    { id: 'export', label: 'Accountant Export', icon: Download },
];

export default function BusinessHealth() {
    const { activeOrgId } = useVertical();
    const [activeTab, setActiveTab] = useState('pnl');
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<ProfitAndLossData | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Default to Current Tax Year (April 6th to now)
    const getTaxYearDates = () => {
        const now = new Date();
        const year = now.getMonth() >= 3 && now.getDate() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
        return {
            start: `${year}-04-06`,
            end: now.toISOString().slice(0, 10)
        };
    };

    const [dateRange, setDateRange] = useState(getTaxYearDates());

    // Average job value — moved here from the Clients page, where it sat as a
    // stat card on a customer database. It answers "am I charging enough?",
    // which is a business-health question, and on the Clients page its
    // explanatory tooltip was hover-only — so on a phone, the number that most
    // needed a definition was the one you could not get a definition for.
    const [avgJobValue, setAvgJobValue] = useState<number | null>(null);

    useEffect(() => {
        if (!activeOrgId) return;
        supabase
            .rpc('fn_get_customer_stats', { p_org_id: activeOrgId })
            .then(({ data: rows, error: statsError }) => {
                if (statsError) {
                    reportError(statsError, 'business-health.fn_get_customer_stats');
                    return;
                }
                const row = (Array.isArray(rows) ? rows[0] : rows) as { avg_job_value?: number } | null;
                setAvgJobValue(row?.avg_job_value ?? 0);
            });
    }, [activeOrgId]);

    useEffect(() => {
        if (!activeOrgId) return;

        const fetchAllData = async () => {
            setLoading(true);
            setError(null);

            try {
                // Fetch P&L
                const pnlResult = await supabase.rpc('fn_get_profit_and_loss', {
                    p_org_id: activeOrgId,
                    p_start_date: dateRange.start,
                    p_end_date: dateRange.end
                });

                if (pnlResult.error) {
                    console.error('Error fetching P&L:', pnlResult.error);
                    setError(pnlResult.error.message);
                } else {
                    setData(pnlResult.data as ProfitAndLossData);
                }
            } catch (err) {
                console.error('Unexpected error:', err);
                setError(errorMessage(err) || 'An unexpected error occurred');
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [activeOrgId, dateRange]);

    return (
        <div className="w-full pb-8">
            <header className="mb-8">
                <h1 className="text-4xl font-black text-fg tracking-tighter mb-2">Fiscal Audit</h1>
                <p className="text-fg-3">Business health, profit analysis, and accountant tools.</p>
            </header>

            {/* Date Range Picker */}
            <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-card rounded-xl border border-line shadow-sm backdrop-blur-sm">
                <span className="text-xs font-bold font-display text-fg-3 uppercase tracking-wider">From</span>
                <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="bg-transparent border border-line rounded-lg px-3 py-2 text-fg text-sm focus:outline-none focus:border-gold transition-colors"
                />
                <span className="text-xs font-bold font-display text-fg-3 uppercase tracking-wider">To</span>
                <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="bg-transparent border border-line rounded-lg px-3 py-2 text-fg text-sm focus:outline-none focus:border-gold transition-colors"
                />
            </div>

            {/* Tabs */}
            <div className="flex bg-card border border-line rounded-lg p-1 w-full md:w-auto overflow-x-auto gap-1 mb-6 shadow-sm" role="tablist" aria-label="Business health reports">
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        className={`flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-md text-sm font-semibold font-display transition-colors whitespace-nowrap shrink-0 flex-none ${activeTab === tab.id
                            ? 'bg-gold-fill/10 text-gold shadow-[inset_0_1px_0_var(--fd-gold-line)] border border-gold/20'
                            : 'text-fg-3 hover:text-fg hover:bg-raised'
                            }`}
                    >
                        <tab.icon className="w-4 h-4" />
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* Average job value. Deliberately outside the tab strip: it is a
                single standing fact about pricing, not a report to page
                through, and unlike the P&L below it is not scoped to the
                selected date range — it is lifetime, which the caption says
                rather than leaving you to assume. */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-card border border-line">
                <div className="p-2.5 rounded-lg bg-gold-fill/10 border border-gold/20 text-gold shrink-0">
                    <TrendingUp className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-fd-3xs font-bold uppercase tracking-wider text-fg-3">
                        Average job value
                    </p>
                    <p className="text-xs text-fg-4 mt-0.5">
                        Every completed job you've invoiced, divided by how many there were. All time, not
                        the range above.
                    </p>
                </div>
                {avgJobValue === null ? (
                    <div className="h-8 w-24 rounded bg-raised animate-pulse shrink-0" />
                ) : (
                    <p className="text-2xl font-bold text-fg tabular-nums shrink-0">
                        {new Intl.NumberFormat('en-GB', {
                            style: 'currency', currency: 'GBP', maximumFractionDigits: 0,
                        }).format(avgJobValue)}
                    </p>
                )}
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 text-accent animate-spin" />
                </div>
            ) : error ? (
                <div className="text-center py-20 bg-danger/10 rounded-xl border border-danger/20">
                    <h3 className="text-lg font-bold text-fg mb-2">Unavailable</h3>
                    <p className="text-fg-3 max-w-md mx-auto mb-4">{error}</p>
                    <p className="text-xs text-fg-4">Please ensure database migrations 20260201 have been applied.</p>
                </div>
            ) : !data ? (
                <div className="text-center text-fg-4 py-20">No data available for this range.</div>
            ) : (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                    {activeTab === 'pnl' && (
                        <ProfitAndLossCard
                            data={data}
                        />
                    )}
                    {activeTab === 'charts' && (
                        <PartsVsLaborChart 
                            orgId={activeOrgId} 
                            startDate={dateRange.start} 
                            endDate={dateRange.end} 
                        />
                    )}
                    {activeTab === 'export' && (
                        <div className="flex flex-col items-center justify-center text-center py-20 px-6">
                            <div className="p-4 bg-gold-fill/10 border border-gold/25 rounded-full mb-5">
                                <Clock className="w-8 h-8 text-gold" />
                            </div>
                            <h3 className="text-lg font-semibold font-display text-fg">
                                Accountant Export — coming soon
                            </h3>
                            <p className="text-sm text-fg-4 mt-2 max-w-md">
                                We're building a proper, accountant-ready export (income, expenses, VAT, credits and
                                drawings, HMRC-aligned). It'll land shortly after launch.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
